<template>
  <div v-if="material && data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('material.color')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownColors"
            :selected-item="selectedColor"
            name="color"
            flat
            :mode="mode"
            size="sm"
            clearable
            @update:selected="handleColorSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('material.size')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownSizes"
            :selected-item="selectedSize"
            name="size"
            flat
            :mode="mode"
            size="sm"
            clearable
            @update:selected="handleSizeSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('material.style')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownStyles"
            :selected-item="selectedStyle"
            name="style"
            flat
            :mode="mode"
            size="sm"
            clearable
            @update:selected="handleStyleSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('material.allowManualPrice')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus
            v-if="mode == 'view'"
            :text-true="$t('common.actions.allowed')"
            :text-false="$t('common.actions.notAllowed')"
            :item="data.allowManualPrice ? data.allowManualPrice : 0" />
          <BaseToggle
            v-else
            :label="$t('material.allowManualPrice')"
            name="allowManualPrice"
            v-model="data.allowManualPrice" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('material.allowZeroValue')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus
            v-if="mode == 'view'"
            :text-true="$t('common.actions.allowed')"
            :text-false="$t('common.actions.notAllowed')"
            :item="data.allowZeroValue ? data.allowZeroValue : 0" />
          <BaseToggle
            v-else
            :label="$t('material.allowZeroValue')"
            name="allowZeroValue"
            v-model="data.allowZeroValue" />
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseToggle from '../common/BaseToggle.vue';
import { type Material } from '@/types/Materials';
import { useLookupsStore } from '@/stores/lookups';
import { onMounted, ref, watch } from 'vue';
import type { SelectDropdownItem } from '@/types/Base';

defineProps<{
  material: Material | undefined;
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
}>();

const data = defineModel<Material>();

const { dropdownColors, dropdownSizes, dropdownStyles } = storeToRefs(useLookupsStore());

const selectedColor = ref<SelectDropdownItem | null>(null);

function handleColorSelection(item: SelectDropdownItem) {
  if (!data.value) return;
  selectedColor.value = item ? item : null;
  data.value.colorCatalogId = item ? (item.value as string) : '';
}

const selectedSize = ref<SelectDropdownItem | null>(null);

function handleSizeSelection(item: SelectDropdownItem) {
  if (!data.value) return;
  selectedSize.value = item ? item : null;
  data.value.sizeCatalogId = item ? (item.value as string) : '';
}

const selectedStyle = ref<SelectDropdownItem | null>(null);

function handleStyleSelection(item: SelectDropdownItem) {
  selectedStyle.value = item ? item : null;
  if (data.value) data.value.styleCatalogId = item ? (item.value as string) : '';
}

function initDropdowns() {
  if (data.value) {
    const colorValue = dropdownColors.value.find(
      (item) => item.value == data.value?.colorCatalog?.id
    );
    if (colorValue) selectedColor.value = colorValue;

    const sizeValue = dropdownSizes.value.find((item) => item.value == data.value?.sizeCatalog?.id);
    if (sizeValue) selectedSize.value = sizeValue;

    const styleValue = dropdownStyles.value.find(
      (item) => item.value == data.value?.styleCatalog?.id
    );
    if (styleValue) selectedStyle.value = styleValue;
  }
}

onMounted(() => {
  initDropdowns();
});

watch(
  () => data.value,
  () => {
    initDropdowns();
  },
  { deep: true }
);
</script>
